import { fmtVND, fmtBonusShort } from "../format.ts";
import { KPI_BONUS, INTERVIEW_TARGET, RTO_TARGET } from "../metrics.ts";
import type { MonthlyKpiRow } from "../metrics.ts";

// ---- legend quy tắc success-hire (#kpi-legend) ----
// Render từ KPI_BONUS để dashboard luôn khớp với engine tính.

export function KpiLegend() {
  return (
    <>
      <span className="legend-title">SUCCESS HIRE BONUS</span>
      {Object.entries(KPI_BONUS).map(([type, bonus]) => (
        <span className="chip" key={type}>
          {type} <small>+{fmtBonusShort(bonus)}</small>
        </span>
      ))}
    </>
  );
}

// ---- các hàng KPI theo recruiter (#kpi-rows) ----

type ProgressCellProps = {
  label: string;
  value: number;
  target: number;
  /** Tiền thưởng hiện ở dòng "Target met" khi đạt mốc. */
  bonusHit: number;
  /** Tag nguồn data, VD "TA LOG". */
  srcTag?: string;
};

function ProgressCell({ label, value, target, bonusHit, srcTag }: ProgressCellProps) {
  const hit = value >= target;
  return (
    <div className="kpi-cell">
      <div className="progress-label">
        <span>
          {label}
          {srcTag ? <>{" "}<span className="src">{srcTag}</span></> : null}
        </span>
        <span><b>{value}</b>/{target}</span>
      </div>
      <div className="progress">
        <i className={hit ? "hit" : ""} style={{ width: `${Math.min(100, (value / target) * 100)}%` }} />
      </div>
      {/*   = &nbsp; của bản cũ — giữ chiều cao dòng khi chưa đạt target */}
      <div className="met">{hit ? `✓ Target met — ${fmtVND(bonusHit)}` : " "}</div>
    </div>
  );
}

export type KpiBenchmarkProps = {
  rows: readonly MonthlyKpiRow[];
  /** null = chưa có tháng nào có data. Cả 2 ca rỗng đều xử lý TRONG component
   *  để React là chủ duy nhất của #kpi-rows — app.js ghi innerHTML vào đó nữa
   *  thì sẽ đánh nhau với React. */
  monthLabelText: string | null;
};

export function KpiBenchmark({ rows, monthLabelText }: KpiBenchmarkProps) {
  if (monthLabelText === null) return <div className="kpi-empty">No data yet.</div>;
  if (!rows.length) return <div className="kpi-empty">No activity for {monthLabelText}.</div>;

  return (
    <>
      {rows.map((x) => (
        <div className="kpi-row" key={x.recruiter}>
          <div className="kpi-who">
            <span className="avatar">{x.recruiter.slice(0, 2).toUpperCase()}</span>
            <span>
              {x.recruiter}
              {x.topupBonus ? <span className="topup">★ TOP-UP +{fmtVND(x.topupBonus)}</span> : null}
            </span>
          </div>

          {/* bonusHit fallback 1000000: khi chưa đạt mốc thì x.interviewsBonus
              = 0, nhưng dòng "Target met" cần hiện SỐ TIỀN SẼ ĐƯỢC nhận. */}
          <ProgressCell
            label="Interviews" value={x.interviews} target={INTERVIEW_TARGET}
            bonusHit={x.interviewsBonus || 1000000}
          />
          <ProgressCell
            label="Ready to Offer" value={x.rto} target={RTO_TARGET}
            bonusHit={x.rtoBonus || 1000000}
            srcTag={x.rtoLive ? "TA LOG" : undefined}
          />

          <div className="kpi-cell">
            <div className="progress-label">
              <span>Success Hires</span>
              <span><b>{x.achievements.length}</b></span>
            </div>
            {x.achievements.length ? (
              <div className="chips">
                {x.achievements.map((a, i) => (
                  <span className="chip" title={a.title || ""} key={`${a.kpiType}-${i}`}>
                    {a.kpiType} <small>+{fmtBonusShort(a.bonus)}</small>
                  </span>
                ))}
              </div>
            ) : (
              <div className="hires-empty">Chưa có hire trong tháng</div>
            )}
            <div className="met">{x.achievementsBonus ? `✓ +${fmtVND(x.achievementsBonus)}` : " "}</div>
          </div>

          <div className="kpi-bonus">
            <div className="amount">{fmtVND(x.totalBonus)}</div>
            <div className="label">EST. BONUS THIS MONTH</div>
            <div className="bonus-lines">
              {([["Interviews", x.interviewsBonus], ["Ready to Offer", x.rtoBonus],
                 ["Success Hires", x.achievementsBonus]] as [string, number][]).map(([label, amount]) => (
                <div className={amount ? "b-line" : "b-line zero"} key={label}>
                  <span>{label}</span><b>{amount ? `+${fmtVND(amount)}` : "0 ₫"}</b>
                </div>
              ))}
              {x.topupBonus ? (
                <div className="b-line"><span>Top-up</span><b>+{fmtVND(x.topupBonus)}</b></div>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
